import { useState, useEffect, useMemo, useCallback } from 'react'
import { parseDateString } from '../../utils/dateUtils'
import { useNavigate } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem
} from '@/components/ui/dropdown-menu'
import { fetchCustomerSalesHistory, fetchCustomerPaymentHistory } from '@/supabase-client'
import { useCreditNotes } from '@/hooks/useCreditNotes'
import { BookOpen, Filter } from 'lucide-react'
import { StatementPreviewModal } from './StatementPreviewModal'
import { GetStatementModal } from './GetStatementModal'

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount)

const PAYMENT_STATUS_VARIANTS = {
  paid: 'default',
  partially_paid: 'secondary',
  unpaid: 'outline',
  voided: 'destructive'
}

const PAYMENT_STATUS_LABELS = {
  paid: 'Paid',
  partially_paid: 'Partial',
  unpaid: 'Unpaid',
  voided: 'Voided'
}

const LIMIT = 50

// ---------------------------------------------------------------------------
// Tray line renderer
// ---------------------------------------------------------------------------
function TrayLine({ item, isMultiBranch, gridClass }) {
  const strain = item.products?.strain || 'Unknown'
  const isBlock = item.block_format?.id != null

  let label
  if (isBlock) {
    label = `${strain} Block (${item.block_format.name}) x${item.quantity}`
  } else {
    const itemNum = item.item_number ? `${item.item_number} ` : ''
    label = `${itemNum}${strain} ${item.quantity}kg`
  }

  const subtotal = parseFloat(item.sub_total) || 0
  const hasDiscount = !!item.discount_note

  return (
    <div className={`grid ${gridClass} gap-2 text-xs text-muted-foreground leading-snug`}>
      {/* Date — empty */}
      <div />
      {/* Branch — empty */}
      {isMultiBranch && <div />}
      {/* Description — item label, indented */}
      <div className="pl-8">{label}</div>
      {/* Debit — subtotal */}
      <div
        className="text-right"
        title={hasDiscount ? item.discount_note : undefined}
      >
        {formatCurrency(subtotal)}{hasDiscount ? ' *' : ''}
      </div>
      {/* Credit — empty */}
      <div />
      {/* Status — empty */}
      <div />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Consignment row (nested under invoice)
// ---------------------------------------------------------------------------
function ConsignmentRow({ sale, isMultiBranch, gridClass }) {
  const [expanded, setExpanded] = useState(false)
  const items = [...(sale.sale_items || [])].sort((a, b) => (b.item_number || 0) - (a.item_number || 0))
  const total = items.reduce((sum, i) => sum + (parseFloat(i.sub_total) || 0), 0)

  return (
    <div>
      <div
        className={`grid ${gridClass} gap-2 px-6 py-1.5 text-sm hover:bg-muted/20 transition-colors items-center cursor-pointer`}
        onClick={() => items.length > 0 && setExpanded(v => !v)}
      >
        {/* Date */}
        <div className="text-xs text-muted-foreground pl-4">
          {parseDateString(sale.date).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short'
          })}
        </div>

        {/* Branch (multi-branch) */}
        {isMultiBranch && (
          <div className="text-xs text-muted-foreground truncate">
            {sale.branches?.name || '—'}
          </div>
        )}

        {/* Description — consignment number, indented */}
        <div className="text-xs pl-4">
          {sale.consignment_no
            ? <span className="font-mono">{sale.consignment_no}</span>
            : <span className="text-muted-foreground italic">No consignment</span>
          }
          {items.length > 0 && (
            <span className="text-muted-foreground ml-1 text-lg leading-none">
              {expanded ? '⌄' : '›'}
            </span>
          )}
        </div>

        {/* Debit */}
        <div className="text-right text-xs font-medium">
          {formatCurrency(total)}
        </div>

        {/* Credit — empty for consignments */}
        <div />

        {/* Status — empty; status shown at invoice level */}
        <div />
      </div>

      {/* Tray lines — collapsible */}
      {expanded && items.length > 0 && (
        <div className="bg-muted/10 px-6 py-1">
          {items.map(item => (
            <TrayLine key={item.id} item={item} isMultiBranch={isMultiBranch} gridClass={gridClass} />
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Invoice group row (parent)
// ---------------------------------------------------------------------------
function InvoiceGroupRow({ invoiceGroup, isMultiBranch, gridClass }) {
  const { invoiceNo, date, invoiceTotal, status, sales } = invoiceGroup
  const navigate = useNavigate()



  return (
    <div className="border-b border-border/50">
      <div
        className={`grid ${gridClass} gap-2 px-6 py-2 text-sm bg-muted/5 hover:bg-muted/20 transition-colors items-center`}
      >
        {/* Date */}
        <div className="text-xs font-medium">
          {parseDateString(date).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short'
          })}
        </div>

        {/* Branch — empty at invoice level (branch shown per consignment) */}
        {isMultiBranch && <div />}

        {/* Description — invoice number */}
        <div className="text-xs">
          {invoiceNo
            ? (
              <button
                onClick={() => navigate(`/invoicing?invoice=${invoiceNo}`)}
                className="text-primary hover:underline focus:outline-none font-mono font-medium"
              >
                {invoiceNo}
              </button>
            )
            : <span className="text-muted-foreground">—</span>
          }
        </div>

        {/* Debit — invoice total */}
        <div className="text-right text-xs font-medium">
          {formatCurrency(invoiceTotal)}
        </div>

        {/* Credit — empty for invoices */}
        <div />

        {/* Status */}
        <div className="flex items-center gap-1 flex-wrap">
          <Badge
            variant={PAYMENT_STATUS_VARIANTS[status] || 'outline'}
            className="text-xs"
          >
            {PAYMENT_STATUS_LABELS[status] || status}
          </Badge>
        </div>
      </div>

      {/* Nested consignments — always visible */}
      {sales.map(sale => (
        <ConsignmentRow
          key={sale.id}
          sale={sale}
          isMultiBranch={isMultiBranch}
          gridClass={gridClass}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Accruing section — uninvoiced sales
// ---------------------------------------------------------------------------
function AccruingSection({ sales, isMultiBranch, gridClass }) {
  if (sales.length === 0) return null

  const total = sales.reduce((sum, sale) => {
    const saleTotal = (sale.sale_items || []).reduce(
      (s, i) => s + (parseFloat(i.sub_total) || 0),
      0
    )
    return sum + saleTotal
  }, 0)

  return (
    <div className="border-b border-border">
      {/* Section header */}
      <div
        className={`grid ${gridClass} gap-2 px-6 py-2 text-sm bg-amber-50/50 border-b border-amber-200/50 items-center`}
      >
        <div className="text-xs font-semibold text-amber-700 col-span-2 flex items-center gap-1.5">
          Accruing
          <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 ml-1">
            {sales.length} sale{sales.length !== 1 ? 's' : ''}
          </Badge>
        </div>
        {isMultiBranch && <div />}
        {/* Debit — accruing total */}
        <div className="text-right text-xs font-semibold text-amber-700">
          {formatCurrency(total)}
        </div>
        {/* Credit — empty */}
        <div />
        {/* Status */}
        <div>
          <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">
            Uninvoiced
          </Badge>
        </div>
      </div>

      {/* Individual uninvoiced sales — always visible */}
      {sales.map(sale => (
        <ConsignmentRow
          key={sale.id}
          sale={sale}
          isMultiBranch={isMultiBranch}
          gridClass={gridClass}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function AccountsSection({ customerId, customerName = '', branches = [] }) {
  const isMultiBranch = branches?.length > 1

  const [showStatementModal, setShowStatementModal] = useState(false)
  const [showGetStatement, setShowGetStatement] = useState(false)
  const [activeFilters, setActiveFilters] = useState(['sale', 'payment', 'credit'])

  // Sales data
  const [sales, setSales] = useState([])
  const [salesHasMore, setSalesHasMore] = useState(false)
  const [salesOffset, setSalesOffset] = useState(0)

  // Payments data
  const [payments, setPayments] = useState([])
  const [paymentsHasMore, setPaymentsHasMore] = useState(false)
  const [paymentsOffset, setPaymentsOffset] = useState(0)

  // Credit notes — fetched via useCreditNotes (no pagination from edge fn for customer list)
  const { creditNotes, isLoading: creditLoading } = useCreditNotes({ customerId })

  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  // Initial load
  useEffect(() => {
    const load = async () => {
      if (!customerId) return
      setLoading(true)
      try {
        const [salesData, paymentsData] = await Promise.all([
          fetchCustomerSalesHistory(customerId, LIMIT, 0),
          fetchCustomerPaymentHistory(customerId, LIMIT, 0)
        ])
        setSales(salesData.sales)
        setSalesHasMore(salesData.hasMore)
        setSalesOffset(LIMIT)

        setPayments(paymentsData.invoices)
        setPaymentsHasMore(paymentsData.hasMore)
        setPaymentsOffset(LIMIT)
      } catch (error) {
        console.error('Error loading accounts:', error)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [customerId])

  // Load more
  const loadMore = useCallback(async () => {
    setLoadingMore(true)
    try {
      const promises = []
      if (salesHasMore && activeFilters.includes('sale')) {
        promises.push(
          fetchCustomerSalesHistory(customerId, LIMIT, salesOffset).then(data => {
            setSales(prev => [...prev, ...data.sales])
            setSalesHasMore(data.hasMore)
            setSalesOffset(prev => prev + LIMIT)
          })
        )
      }
      if (paymentsHasMore && activeFilters.includes('payment')) {
        promises.push(
          fetchCustomerPaymentHistory(customerId, LIMIT, paymentsOffset).then(data => {
            setPayments(prev => [...prev, ...data.invoices])
            setPaymentsHasMore(data.hasMore)
            setPaymentsOffset(prev => prev + LIMIT)
          })
        )
      }
      await Promise.all(promises)
    } catch (error) {
      console.error('Error loading more:', error)
    } finally {
      setLoadingMore(false)
    }
  }, [customerId, salesHasMore, paymentsHasMore, salesOffset, paymentsOffset, activeFilters])

  const hasMore = (salesHasMore && activeFilters.includes('sale')) ||
    (paymentsHasMore && activeFilters.includes('payment'))

  // ---------------------------------------------------------------------------
  // Derived data — split into accruing vs grouped
  // ---------------------------------------------------------------------------
  const { accruingSales, monthGroups } = useMemo(() => {
    // 1. Uninvoiced sales (invoice_id is null)
    const uninvoiced = activeFilters.includes('sale')
      ? sales.filter(s => {
          if (s.invoice_id != null) return false
          if (s.status === 'invoiced' || s.status === 'paid') return false
          // Exclude £0 sales (samples/gifts with no value)
          const saleTotal = (s.sale_items || []).reduce((sum, i) => sum + (parseFloat(i.sub_total) || 0), 0)
          return saleTotal > 0
        })
      : []

    // 2. Invoiced sales — group by invoice_id
    const invoicedSalesMap = new Map() // invoice_id → Sale[]
    if (activeFilters.includes('sale')) {
      sales
        .filter(s => s.invoice_id != null)
        .forEach(sale => {
          if (!invoicedSalesMap.has(sale.invoice_id)) {
            invoicedSalesMap.set(sale.invoice_id, [])
          }
          invoicedSalesMap.get(sale.invoice_id).push(sale)
        })
    }

    // 3. Build a map of payments by invoice_id for quick lookup
    const paymentMap = new Map(payments.map(p => [p.invoice_id, p]))

    // 4. Build invoice group records (one per unique invoice_id that has sales)
    const invoiceGroupRecords = []
    if (activeFilters.includes('sale') || activeFilters.includes('payment')) {
      invoicedSalesMap.forEach((salesForInvoice, invoiceId) => {
        const payment = paymentMap.get(invoiceId)
        // Derive total from sale items if payment record is missing
        const invoiceTotal = payment?.invoice_total
          ?? salesForInvoice.reduce((sum, sale) =>
            sum + (sale.sale_items || []).reduce(
              (s, i) => s + (parseFloat(i.sub_total) || 0), 0
            ), 0)

        // Date: prefer the invoice_balances date, fall back to earliest sale date
        const date = payment?.date
          ?? salesForInvoice.map(s => s.date).sort()[0]

        invoiceGroupRecords.push({
          id: `invoice-group-${invoiceId}`,
          type: 'invoice-group',
          date,
          invoiceId,
          invoiceNo: payment?.invoice_no ?? salesForInvoice[0]?.invoices?.invoice_no ?? null,
          invoiceTotal,
          status: payment?.calculated_status ?? 'unpaid',
          sales: salesForInvoice.sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0))
        })
      })
    }

    // 4b. Legacy sales — invoiced/paid but no invoice_id (pre-Xero)
    //     Show as standalone invoice groups using legacy_invoice_no
    if (activeFilters.includes('sale')) {
      const legacySales = sales.filter(s => s.invoice_id == null && (s.status === 'invoiced' || s.status === 'paid'))
      // Group legacy sales by legacy_invoice_no (or individually if no invoice number)
      const legacyMap = new Map()
      legacySales.forEach(sale => {
        const key = sale.legacy_invoice_no || `legacy-${sale.id}`
        if (!legacyMap.has(key)) legacyMap.set(key, [])
        legacyMap.get(key).push(sale)
      })
      legacyMap.forEach((salesForGroup, key) => {
        const invoiceTotal = salesForGroup.reduce((sum, sale) =>
          sum + (sale.sale_items || []).reduce((s, i) => s + (parseFloat(i.sub_total) || 0), 0), 0)
        const date = salesForGroup.map(s => s.date).sort().reverse()[0]
        invoiceGroupRecords.push({
          id: `legacy-${key}`,
          type: 'invoice-group',
          date,
          invoiceId: null,
          invoiceNo: salesForGroup[0]?.legacy_invoice_no || null,
          invoiceTotal,
          status: salesForGroup.every(s => s.status === 'paid') ? 'paid' : 'invoiced',
          sales: salesForGroup.sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0))
        })
      })
    }

    // 5. Orphan payment rows — invoices in payments that have NO sales in our loaded data
    //    (edge case; e.g. legacy invoices, or sales not yet loaded)
    const orphanPaymentRecords = []
    if (activeFilters.includes('payment')) {
      payments.forEach(inv => {
        if (!invoicedSalesMap.has(inv.invoice_id)) {
          orphanPaymentRecords.push({
            id: `orphan-payment-${inv.invoice_id}`,
            type: 'orphan-payment',
            date: inv.date,
            invoiceNo: inv.invoice_no,
            invoiceTotal: inv.invoice_total || 0,
            status: inv.calculated_status || inv.status || 'unpaid'
          })
        }
      })
    }

    // 6. Credit note records
    const creditRecords = activeFilters.includes('credit')
      ? creditNotes.map(cn => ({
          id: `credit-${cn.id}`,
          type: 'credit',
          date: cn.date,
          description: cn.credit_note_no || null,
          amount: parseFloat(cn.amount),
          reason: cn.reason,
          creditType: cn.type,
          trayNumber: cn.tray_number || null,
          consignmentNo: cn.consignment_no || null
        }))
      : []

    // 6b. Payment rows — only for invoices where money has been received
    const paymentRecords = []
    if (activeFilters.includes('payment')) {
      payments.forEach(inv => {
        const amountPaid = parseFloat(inv.amount_paid || 0)
        if (amountPaid > 0) {
          paymentRecords.push({
            id: `payment-${inv.invoice_id}`,
            type: 'payment',
            date: inv.date,
            invoiceNo: inv.invoice_no,
            amount: amountPaid,
            status: inv.calculated_status || inv.status || 'unpaid'
          })
        }
      })
    }

    // 7. Combine everything except uninvoiced for month grouping
    const allGrouped = [
      ...invoiceGroupRecords,
      ...orphanPaymentRecords,
      ...paymentRecords,
      ...creditRecords
    ].sort((a, b) => {
      if (a.date > b.date) return -1
      if (a.date < b.date) return 1
      return 0
    })

    // 8. Group by month/year
    const groups = {}
    allGrouped.forEach(record => {
      const date = parseDateString(record.date)
      const monthYear = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      if (!groups[monthYear]) groups[monthYear] = []
      groups[monthYear].push(record)
    })

    return {
      accruingSales: uninvoiced.sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0)),
      monthGroups: groups
    }
  }, [sales, payments, creditNotes, activeFilters])

  const toggleFilter = (type) => {
    setActiveFilters(prev => {
      if (prev.includes(type)) {
        if (prev.length === 1) return prev
        return prev.filter(t => t !== type)
      }
      return [...prev, type]
    })
  }

  const totalGroupCount = Object.values(monthGroups).reduce((sum, recs) => sum + recs.length, 0)
  const totalCount = totalGroupCount + accruingSales.length

  // Grid layout — Date | [Branch] | Description | Debit | Credit | Status
  const gridClass = isMultiBranch
    ? 'grid-cols-[80px_80px_1fr_90px_90px_100px]'
    : 'grid-cols-[80px_1fr_90px_90px_100px]'

  if (loading || creditLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        </CardContent>
      </Card>
    )
  }

  const isEmpty = accruingSales.length === 0 && totalGroupCount === 0

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Accounts {totalCount > 0 && `(${totalCount})`}
          </CardTitle>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-3.5 w-3.5 mr-1.5" />
                  Filter
                  {activeFilters.length < 3 && (
                    <Badge variant="secondary" className="ml-1.5 text-xs px-1.5 py-0">
                      {activeFilters.length}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuCheckboxItem
                  checked={activeFilters.includes('sale')}
                  onCheckedChange={() => toggleFilter('sale')}
                >
                  Sales
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={activeFilters.includes('payment')}
                  onCheckedChange={() => toggleFilter('payment')}
                >
                  Payments
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={activeFilters.includes('credit')}
                  onCheckedChange={() => toggleFilter('credit')}
                >
                  Credit Notes
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGetStatement(true)}
            >
              Get Statement
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowStatementModal(true)}
            >
              Send Statement
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? (
          <div className="text-center py-8 text-muted-foreground px-6">
            <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No records found</p>
          </div>
        ) : (
          <>
            {/* Column headers */}
            <div className={`grid ${gridClass} gap-2 px-6 py-2 text-xs font-medium text-muted-foreground border-b bg-muted/30`}>
              <div>Date</div>
              {isMultiBranch && <div>Branch</div>}
              <div>Description</div>
              <div className="text-right">Debit</div>
              <div className="text-right">Credit</div>
              <div>Status</div>
            </div>

            {/* Accruing section */}
            {accruingSales.length > 0 && activeFilters.includes('sale') && (
              <AccruingSection
                sales={accruingSales}
                isMultiBranch={isMultiBranch}
                gridClass={gridClass}
              />
            )}

            {/* Month-grouped invoiced records */}
            {Object.entries(monthGroups).map(([monthYear, records]) => (
              <div key={monthYear}>
                <div className="sticky top-0 z-10 bg-white border-b px-6 py-1.5">
                  <span className="text-xs font-semibold text-muted-foreground">{monthYear}</span>
                </div>

                {records.map(record => {
                  if (record.type === 'invoice-group') {
                    return (
                      <InvoiceGroupRow
                        key={record.id}
                        invoiceGroup={record}
                        isMultiBranch={isMultiBranch}
                        gridClass={gridClass}
                      />
                    )
                  }

                  if (record.type === 'orphan-payment') {
                    return (
                      <OrphanPaymentRow
                        key={record.id}
                        record={record}
                        isMultiBranch={isMultiBranch}
                        gridClass={gridClass}
                      />
                    )
                  }

                  if (record.type === 'payment') {
                    return (
                      <PaymentRow
                        key={record.id}
                        record={record}
                        isMultiBranch={isMultiBranch}
                        gridClass={gridClass}
                      />
                    )
                  }

                  if (record.type === 'credit') {
                    return (
                      <CreditNoteRow
                        key={record.id}
                        record={record}
                        isMultiBranch={isMultiBranch}
                        gridClass={gridClass}
                      />
                    )
                  }

                  return null
                })}
              </div>
            ))}

            {hasMore && (
              <div className="py-3 flex justify-center border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      <StatementPreviewModal
        open={showStatementModal}
        onOpenChange={setShowStatementModal}
        customerId={customerId}
        customerName={customerName}
        branches={branches}
      />
      <GetStatementModal
        open={showGetStatement}
        onOpenChange={setShowGetStatement}
        customerId={customerId}
        customerName={customerName}
      />
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Orphan payment row (invoice with no matching sales loaded)
// ---------------------------------------------------------------------------
function OrphanPaymentRow({ record, isMultiBranch, gridClass }) {
  const navigate = useNavigate()


  return (
    <div
      className={`grid ${gridClass} gap-2 px-6 py-2 text-sm border-b border-border/50 hover:bg-muted/30 transition-colors items-center`}
    >
      <div className="text-xs">
        {parseDateString(record.date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short'
        })}
      </div>

      {isMultiBranch && <div />}

      {/* Description — invoice number */}
      <div className="text-xs">
        {record.invoiceNo
          ? (
            <button
              onClick={() => navigate(`/invoicing?invoice=${record.invoiceNo}`)}
              className="text-primary hover:underline focus:outline-none font-mono"
            >
              {record.invoiceNo}
            </button>
          )
          : <span className="text-muted-foreground">—</span>
        }
      </div>

      {/* Debit — invoice total */}
      <div className="text-right text-xs font-medium">
        {formatCurrency(record.invoiceTotal)}
      </div>

      {/* Credit — empty */}
      <div />

      {/* Status */}
      <div className="flex items-center gap-1 flex-wrap">
        <Badge
          variant={PAYMENT_STATUS_VARIANTS[record.status] || 'outline'}
          className="text-xs"
        >
          {PAYMENT_STATUS_LABELS[record.status] || record.status}
        </Badge>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Credit note row
// ---------------------------------------------------------------------------
function PaymentRow({ record, isMultiBranch, gridClass }) {
  const navigate = useNavigate()


  return (
    <div
      className={`grid ${gridClass} gap-2 px-6 py-2 text-sm border-b border-border/50 hover:bg-muted/30 transition-colors items-center`}
    >
      <div className="text-xs">
        {parseDateString(record.date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short'
        })}
      </div>

      {isMultiBranch && <div />}

      <div className="text-xs">
        <span className="text-muted-foreground">Payment </span>
        {record.invoiceNo
          ? (
            <button
              onClick={() => navigate(`/invoicing?invoice=${record.invoiceNo}`)}
              className="text-primary hover:underline focus:outline-none font-mono"
            >
              {record.invoiceNo}
            </button>
          )
          : <span className="text-muted-foreground">—</span>
        }
      </div>

      {/* Debit — empty */}
      <div />

      {/* Credit — payment amount */}
      <div className="text-right text-xs font-medium">
        {formatCurrency(record.amount)}
      </div>

      {/* Status */}
      <div className="flex items-center gap-1 flex-wrap">
        <Badge
          variant={PAYMENT_STATUS_VARIANTS[record.status] || 'outline'}
          className="text-xs"
        >
          {PAYMENT_STATUS_LABELS[record.status] || record.status}
        </Badge>
      </div>
    </div>
  )
}

function CreditNoteRow({ record, isMultiBranch, gridClass }) {
  return (
    <div
      className={`grid ${gridClass} gap-2 px-6 py-2 text-sm border-b border-border/50 hover:bg-muted/30 transition-colors items-center`}
    >
      <div className="text-xs">
        {parseDateString(record.date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short'
        })}
      </div>

      {isMultiBranch && <div />}

      {/* Description — credit note number with tooltip */}
      <div className="text-xs">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="font-mono cursor-default">
                {record.description || '—'}
              </span>
            </TooltipTrigger>
            {(record.consignmentNo || record.trayNumber) && (
              <TooltipContent>
                <div className="text-xs">
                  {record.consignmentNo && <div>{record.consignmentNo}</div>}
                  {record.trayNumber && <div>Tray {record.trayNumber}</div>}
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Debit — empty */}
      <div />

      {/* Credit — credit note amount */}
      <div className="text-right text-xs font-medium text-amber-600">
        {formatCurrency(record.amount)}
      </div>

      {/* Status */}
      <div>
        <Badge variant="outline" className="text-xs">
          {record.creditType}
        </Badge>
      </div>
    </div>
  )
}
